import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Upload, Package } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { Button } from "./ui/button";
import { HeroUploadForm } from "./HeroUploadForm";
import { HeroImageGrid } from "./hero-manager/HeroImageGrid";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

interface HeroImage {
  id: number;
  title: string;
  description: string;
  image_url: string;
  button_text: string;
  button_url: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export const HeroManager = () => {
  const [uploading, setUploading] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch featured packages
  const { data: packages = [] } = useQuery({
    queryKey: ["packages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sns_packages')
        .select('*')
        .order('id', { ascending: true });
      
      if (error) throw error;
      return data;
    },
  });

  // Toggle featured status
  const toggleFeatured = useMutation({
    mutationFn: async ({ id, is_featured }: { id: number; is_featured: boolean }) => {
      const { error } = await supabase
        .from('sns_packages')
        .update({ is_featured: !is_featured })
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["packages"] });
      toast({
        title: "Success",
        description: "Package featured status updated",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update package: " + error.message,
        variant: "destructive",
      });
    },
  });

  const { data: images = [], isLoading } = useQuery({
    queryKey: ["hero-images"],
    queryFn: async () => {
      console.log("Fetching hero images...");
      const { data, error } = await supabase
        .from('sns_hero_images')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error("Error fetching hero images:", error);
        throw error;
      }
      
      console.log("Hero images fetched successfully:", data);
      return data as HeroImage[];
    },
  });

  const handleUpload = async (file: File, altText: string) => {
    try {
      console.log("Starting image upload...");
      setUploading(true);

      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('hero-images')
        .upload(fileName, file);

      if (uploadError) {
        throw uploadError;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('hero-images')
        .getPublicUrl(fileName);

      const { error: insertError } = await supabase
        .from('sns_hero_images')
        .insert([
          {
            image_url: publicUrl,
            title: altText,
            description: altText,
            is_active: true,
            sort_order: 1
          }
        ]);

      if (insertError) {
        throw insertError;
      }

      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });
      
      setShowUploadForm(false);
      queryClient.invalidateQueries({ queryKey: ["hero-images"] });
    } catch (error: any) {
      console.error("Upload error:", error);
      toast({
        title: "Error",
        description: "Failed to upload image: " + error.message,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('sns_hero_images')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["hero-images"] });
      toast({
        title: "Success",
        description: "Image deleted successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to delete image: " + error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">Hero Section Configuration</h2>
      
      <Tabs defaultValue="images" className="w-full">
        <TabsList>
          <TabsTrigger value="images">Hero Images</TabsTrigger>
          <TabsTrigger value="packages">Featured Packages</TabsTrigger>
        </TabsList>
        
        <TabsContent value="images" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold">Manage Hero Images</h3>
            <Button
              onClick={() => setShowUploadForm(!showUploadForm)}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {showUploadForm ? 'Cancel Upload' : 'Upload Image'}
            </Button>
          </div>
          
          {showUploadForm && (
            <HeroUploadForm onUpload={handleUpload} uploading={uploading} />
          )}

          <HeroImageGrid
            images={images}
            isLoading={isLoading}
            deleteImage={deleteImage}
          />
        </TabsContent>
        
        <TabsContent value="packages" className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-semibold">Featured Packages in Hero</h3>
            <Button className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Configure Features
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {packages.map((pkg) => (
              <Card key={pkg.id} className="hover:shadow-md transition-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">{pkg.name}</CardTitle>
                  <CardDescription>{pkg.price}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    {pkg.includes?.slice(0, 2).join(', ')}...
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded ${
                      pkg.is_featured ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {pkg.is_featured ? 'Featured in Hero' : 'Not Featured'}
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => toggleFeatured.mutate({ id: pkg.id, is_featured: pkg.is_featured })}
                      disabled={toggleFeatured.isPending}
                    >
                      {pkg.is_featured ? 'Remove from Hero' : 'Add to Hero'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};